import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { Mail } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/client/components/ui/card";
import { Input } from "@/client/components/ui/input";
import { Button } from "@/client/components/ui/button";
import { Label } from "@/client/components/ui/label";
import { api } from "@/client/lib/api";

const loginSchema = z.object({
  email: z.email("Vul een geldig e-mailadres in"),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  async function onSubmit(data: LoginForm) {
    setError(null);
    try {
      const res = await api.api.auth.login.$post({
        json: { email: data.email },
      });
      if (res.ok) {
        setSubmitted(true);
      } else if (res.status === 503) {
        setError("Het versturen van de inloglink is niet gelukt. Neem contact op met de beheerder of probeer het later nog een keer.");
      } else if (res.status === 429) {
        setError("Te veel inlogpogingen. Probeer het later nog een keer.");
      } else if (res.status === 404) {
        setError("Dit e-mailadres is niet bij ons bekend. Neem contact op met KUVO Woerden als u denkt dat dit niet klopt.");
      } else {
        const body = await res.json();
        setError("error" in body ? String(body.error) : "Er ging iets mis. Probeer het opnieuw.");
      }
    } catch {
      setError("Kan geen verbinding maken met de server.");
    }
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-primary/10">
              <Mail className="size-6 text-primary" />
            </div>
            <CardTitle>Controleer uw e-mail</CardTitle>
            <CardDescription>
              We hebben een inloglink naar uw e-mailadres gestuurd. Klik op de link om in te loggen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-center text-sm text-muted-foreground">
              De link is 15 minuten geldig. Niet ontvangen? Controleer uw spammap of probeer het opnieuw.
            </p>
          </CardContent>
          <CardFooter className="justify-center">
            <Button variant="ghost" onClick={() => setSubmitted(false)}>
              Opnieuw proberen
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle>Inloggen</CardTitle>
          <CardDescription>
            Mijn KUVO Woerden
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">E-mailadres</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="naam@voorbeeld.nl"
                  autoComplete="email"
                  autoFocus
                  {...register("email")}
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
              <Button type="submit" className="mt-1 w-full" disabled={isSubmitting}>
                {isSubmitting ? "Bezig..." : "Inloglink versturen"}
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}
